import { NgModule, NgModuleFactory, ModuleWithProviders, Injector, StaticProvider, Compiler } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { LayoutComponent } from './layout.component';
import { AuthGuard } from '../security/guard/auth.guard';
import { PrincipalService } from '../security/authentication/principal.service';
import { environment } from 'src/environments/environment.prod';

/* Returns a NgModule with its providers and class overrides */
class LazyNgModuleWithProvidersFactory<T> extends NgModuleFactory<T> {
  constructor(private moduleWithProviders: ModuleWithProviders<T>) {
    super();
  }

  get moduleType() {
    return this.moduleWithProviders.ngModule;
  }

  create(parentInjector: Injector | null) {
    const injector = Injector.create({
      providers: this.moduleWithProviders.providers as StaticProvider[],
      parent: parentInjector
    });

    const compiler = injector.get(Compiler);
    const factory = compiler.compileModuleSync(this.moduleType);

    return factory.create(injector);
  }
}

const routes: Routes = [
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'organigrama',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_ORG_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/organigram/organigram.module').then(m => m.OrganigramModule)
      },
      {
        path: 'catalogo',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_CAT_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/catalogue/catalogue.module').then(m => m.CatalogueModule)
      },
      {
        path: 'formularios',
        loadChildren: () =>
          import('../../features/form-generator/form-generator.module').then(
            m => m.FormGeneratorModule
          )
      },
      {
        path: 'diccionarios',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_ADMIN'] },
        loadChildren: () =>
          import('../../features/dictionary-management/dictionary-management.module').then(
            m => m.DictionaryManagementModule
          )
      },
      {
        path: 'usuarios',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_USRMNT_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/user-management/user-management.module').then(
            m => m.UserManagementModule
          )
      },
      {
        path: 'usuarios-ticketing',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_USRMNT_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/user-ticketing-management/user-ticketing-management.module').then(
            m => m.UserTicketingManagementModule
          )
      },
      {
        path: 'grupos-ticketing',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_USRMNT_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/group-ticketing-management/group-ticketing-management.module').then(
            m => m.GroupTicketingManagementModule
          )
      },
      {
        path: 'incidencias',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_INC_VISUALIZACION'] },
        loadChildren: () => import('../../features/issues/issues.module').then(m => m.IssuesModule)
      },
      {
        path: 'inventario',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_INV_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/inventory/inventory.module').then(m => m.InventoryModule)
      },
      {
        path: 'inventario/:itemClass',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_INV_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/inventory/inventory.module').then(m => m.InventoryModule)
      },
      {
        path: 'inventario-sede',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_INV_VISUALIZACION'] },
        loadChildren: () =>
        import('../../features/inventory-headquarters/inventory-headquarters.module').then(m => m.InventoryHeadquartersModule)
      },
      {
        path: 'inventario-servicio',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_INV_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/inventory-service/inventory-service.module').then(m => m.InventoryServiceModule)
      },
      {
        path: 'inventario-equipo',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_INV_VISUALIZACION'] },
        loadChildren: () =>
          import('../../features/inventory-equipment/inventory-equipment.module').then(m => m.InventoryEquipmentModule)
      },
      {
        path: 'pedidos',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_PED_VISUALIZACION'] },
        loadChildren: () => import('../../features/orders/orders.module').then(m => m.OrdersModule)
      },
      {
        path: 'avisos',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: [] },
        loadChildren: () =>
          import('../../features/notifications/notifications.module').then(
            m => m.NotificationsModule
          )
      },
      {
        path: 'trabajos-programados',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: [] },
        loadChildren: () =>
          import('../../features/scheduled-jobs/scheduled-jobs.module').then(
            m => m.ScheduledJobsModule
          )
      },
      {
        path: 'estadisticas',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: [] },
        loadChildren: () =>
          import('../../features/statistics/statistics.module').then(m => m.StatisticsModule)
      },
      {
        path: 'form-editor',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: [] },
        loadChildren: () =>
          import('../../features/dyn-form-editor/dyn-form-editor.module').then(
            m => m.DynFormEditorModule
          )
      },
      {
        path: 'acciones',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_ADMIN'] },
        loadChildren: () =>
          import('../../features/actions-management/actions-management.module').then(
            m => m.ActionsManagementModule
        )
      },
      {
        path: 'contactos',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: [] },
        loadChildren: () =>
          import('../../features/contacts-book/contacts-book.module').then(
            m => m.ContactsBookModule

          )
      },
      {
        path: 'facturacion',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_GESTION_FACTURAS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.BillingModule.loadConfig(environment))
          )
      },
      {
        path: 'listadoFacturasPeriodo',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_LISTADO_PERIODO_FACTURAS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.PeriodBillingPeriodModule.loadConfig(environment))
          )
      },
      {
        path: 'consumosMultiperiodo',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_CONSUMOS_MULTIPERIODO'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.MultiBillingPeriodModule.loadConfig(environment))
          )
      },
      {
        path: 'informeResumenConsumos',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_RESUMEN_CONSUMOS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.ConsumptionSummaryModule.loadConfig(environment))
          )
      },
      {
        path: 'reportByService',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INFORME_SERVICIOS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.ReportByServiceModule.loadConfig(environment))
          )
      },
      {
        path: 'detalleOtrosCargos',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_OTROS_CARGOS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.OtherCostManagementModule.loadConfig(environment))
          )
      },
      {
        path: 'facturas/generarFacturas/listado',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_ESTADO_GENERACION_FACTURAS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.InvoicesGenerationManagementModule.loadConfig(environment))
          )
      },
      {
        path: 'listadoInformeSeguimientoTarifasPlana',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INFORME_TARIFAS_PLANAS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.ReportFlatRatesPeriodModule.loadConfig(environment))
          )
      },
      {
        path: 'informeTarifasPlanaCECO',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INFORME_TARIFAS_PLANAS_ENTIDAD'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.ReportFlatRatesCECOPeriodModule.loadConfig(environment))
          )
      },
      {
        path: 'listadoProcesoFacturacion',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_GESTION_FACTURACION'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.BillingProcessModule.loadConfig(environment))
          )
      },
      {
        path: 'listadoTrazasProcesoFacturacion',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_TRAZAS_PROCESOS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.TracesBillingProcessModule.loadConfig(environment))
          )
      },
      {
        path: 'detalleConfLlamadasInternacionales',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INTERNACIONAL'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.InternationalCallSettingsModule.loadConfig(environment))
          )
      },
      {
        path: 'detalleConfRoaming',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_ROAMING'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.RoamingSettingsModule.loadConfig(environment))
          )
      },
      {
        path: 'detallePermisosUsuario',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_PERMISOS_USUARIOS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.BillingUserPermissionsModule.loadConfig(environment))
          )
      }
      ,
      {
        path: 'detalleCatalogue',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_CATALOGO'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.CatalogueModule.loadConfig(environment))
          )
      },
      {
        path: 'detalleConfCatalogoPer',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_CAT_PER'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.CatPerSettingsModule.loadConfig(environment))
          )
      },
      {
        path: 'detalleConfDestinos',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_DESTINOS'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m => new LazyNgModuleWithProvidersFactory(m.DestinySettingsModule.loadConfig(environment))
          )
      },
      {
        path: 'informeFactServicioEmitido',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INF_SERV_EMITIDO'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.ReportByServiceIssuedModule.loadConfig(environment))
          )
      },
      {
        path: 'informeFactServicioReal',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INF_SERV_REAL'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.ReportByServiceRealModule.loadConfig(environment))
          )
      },
      {
        path: 'informeModInventario',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INF_MOD_INVENTARIO'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.ReportChangeInventoryModule.loadConfig(environment))
          )
      },
      {
        path: 'informeFactCuenta',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INF_CUENTA_FACT'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.ReportByBillableAccountModule.loadConfig(environment))
          )
      },
      {
        path: 'facturacionEntidad',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_GESTION_FACTURAS_ENT'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.BillingEntityModule.loadConfig(environment))
          )
      },
      {
        path: 'informeModServicio',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_INF_MOD_SERVICIO'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.ReportChangeServiceModule.loadConfig(environment))
          )
      },
      {
        path: 'uploadFile',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_F_UPLOAD'] },
        loadChildren: () =>
          import('@dekra/billing-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.FileUploadModule.loadConfig(environment))
          )
      },
      {
        path: 'detalleGeneraInformes',
        canLoad: [AuthGuard],
        data: { requiredAuthorities: ['ROLE_GEN_INF'] },
        loadChildren: () =>
          import('@dekra/generainformes-lib').then(
            m =>  new LazyNgModuleWithProvidersFactory(m.GenerateReportsModule.loadConfig(environment))
          )
      }
    ],
    /* Se pasa la clase encargada de devolver los roles que pertenecen al usuario, que se recogerán
    en el objeto 'data', para su uso dentro de los componentes de los módulos. */
    resolve: { authorities: PrincipalService }
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class LayoutRoutingModule {}
